import { useState, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Card,
  Descriptions,
  Tag,
  Space,
  Row,
  Col,
  Breadcrumb,
  Button,
  Modal,
  Form,
  message,
  Tooltip,
} from 'antd'
import {
  HomeOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  EditOutlined,
  CodeOutlined,
} from '@ant-design/icons'
import { hospitalsApi } from '@/api/hospitals'
import { HospitalMap } from '@/components/hospitals/HospitalMap'
import { HospitalFeatures } from '@/components/hospitals/HospitalFeatures'
import { HospitalCapabilities } from '@/components/hospitals/HospitalCapabilities'
import { HospitalFaxNumbers } from '@/components/hospitals/HospitalFaxNumbers'
import { HospitalForm } from '@/components/hospitals/HospitalForm'
import { AssignedSenders } from '@/components/hospitals/AssignedSenders'
import { SSOConfiguration } from '@/components/hospitals/SSOConfiguration'
import { usePageHeader } from '@/hooks/usePageHeader'
import { PageLoading } from '@/components/PageLoading'
import { getApiErrorMessage } from '@/utils/error'
import type { Hospital } from '@/types/hospital'

export const HospitalDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isRawModalOpen, setIsRawModalOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['hospital', id],
    queryFn: () => hospitalsApi.getHospital(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (values: Partial<Hospital>) => hospitalsApi.updateHospital(id!, values),
    onSuccess: () => {
      message.success('Hospital updated successfully')
      queryClient.invalidateQueries({ queryKey: ['hospital', id] })
      setIsEditModalOpen(false)
      form.resetFields()
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to update hospital'))
    },
  })

  const handleModalClose = () => {
    setIsEditModalOpen(false)
    form.resetFields()
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()

      // Merge facility_type_custom into facility_type if "Other" is selected
      if (values.facility_type === 'Other' && values.facility_type_custom) {
        values.facility_type = values.facility_type_custom
        delete values.facility_type_custom
      } else {
        // Clean up the custom field if it exists but wasn't used
        delete values.facility_type_custom
      }

      updateMutation.mutate(values)
    } catch (error) {
      console.error('Validation failed:', error)
    }
  }

  const handleViewRaw = () => {
    setIsRawModalOpen(true)
  }

  const handleRawModalClose = () => {
    setIsRawModalOpen(false)
  }

  // Get hospital data (may be undefined during loading)
  const hospital = data?.data

  // Handle edit modal - must be after hospital is defined
  const handleEdit = useCallback(() => {
    if (hospital) {
      // Handle facility_type initialization for the dropdown
      const facilityType = hospital.facility_type
      const predefinedTypes = ['Hospital', 'mobile assessment team']

      if (facilityType && predefinedTypes.includes(facilityType)) {
        // It's a predefined type
        form.setFieldsValue({
          ...hospital,
          facility_type: facilityType,
          facility_type_custom: undefined,
        })
      } else if (facilityType) {
        // It's a custom type - set to "Other" and put value in custom field
        form.setFieldsValue({
          ...hospital,
          facility_type: 'Other',
          facility_type_custom: facilityType,
        })
      } else {
        // No facility type set
        form.setFieldsValue(hospital)
      }

      setIsEditModalOpen(true)
    }
  }, [hospital, form])

  // Truncate long hospital names for display in header
  const truncateHospitalName = (name: string | null | undefined, maxLength = 60) => {
    if (!name) return ''
    if (name.length <= maxLength) return name
    return name.slice(0, maxLength) + '...'
  }

  // Memoize page header config - hooks must be at top level, before any returns
  const pageHeaderConfig = useMemo(() => {
    if (!hospital) {
      return {} // Empty config while loading or if hospital not found
    }

    const truncatedName = truncateHospitalName(hospital.name)
    const isNameTruncated = hospital.name && hospital.name.length > 60

    return {
      breadcrumb: (
        <Breadcrumb
          items={[
            {
              title: (
                <a onClick={() => navigate('/hospitals')}>
                  <HomeOutlined /> Hospitals
                </a>
              ),
            },
            {
              title: truncatedName,
            },
          ]}
        />
      ),
      title: (
        <Space>
          {isNameTruncated ? (
            <Tooltip title={hospital.name}>
              <h1 style={{ margin: 0 }}>{truncatedName}</h1>
            </Tooltip>
          ) : (
            <h1 style={{ margin: 0 }}>{truncatedName}</h1>
          )}
          <Tag color="blue">CMS: {hospital.cmsid}</Tag>
        </Space>
      ),
      extra: (
        <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
          Edit Hospital
        </Button>
      ),
    }
  }, [hospital, navigate, handleEdit])

  // Set page header in main AppLayout header (must be called unconditionally)
  usePageHeader(pageHeaderConfig)

  const copyToClipboard = () => {
    if (hospital) {
      navigator.clipboard.writeText(JSON.stringify(hospital, null, 2))
      message.success('Copied to clipboard')
    }
  }

  // Early returns AFTER all hooks
  if (isLoading) {
    return <PageLoading />
  }

  if (!hospital) {
    return <div>Hospital not found</div>
  }

  // Format full address
  const formatAddress = () => {
    const parts = [hospital.address, hospital.city, hospital.state, hospital.zip].filter(Boolean)
    return parts.join(', ')
  }

  // Collect all features
  const features = [
    hospital.participant && { label: 'Participant', color: 'blue' },
    hospital.intake && { label: 'Intake', color: 'green' },
    hospital.receiver && { label: 'Receiver', color: 'cyan' },
    hospital.is_transfer_center && { label: 'Transfer Center', color: 'purple' },
    hospital.critical_access && { label: 'Critical Access', color: 'orange' },
    hospital.emergency && { label: 'Emergency', color: 'red' },
    hospital.cfr && { label: 'CFR', color: 'magenta' },
  ].filter(Boolean) as { label: string; color: string }[]

  return (
    <div>

      <Row gutter={24}>
        {/* Left Column - 60% */}
        <Col xs={24} lg={14}>
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            {/* Basic Information Card */}
            <Card title="Basic Information">
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Name">
                  <strong>{hospital.name}</strong>
                </Descriptions.Item>
                {hospital.type && (
                  <Descriptions.Item label="Type">{hospital.type}</Descriptions.Item>
                )}
                {hospital.facility_type && (
                  <Descriptions.Item label="Facility Type">
                    {hospital.facility_type}
                  </Descriptions.Item>
                )}
                {hospital.market_name && (
                  <Descriptions.Item label="Market">{hospital.market_name}</Descriptions.Item>
                )}
                <Descriptions.Item label="Address">{formatAddress()}</Descriptions.Item>
                {hospital.phone && (
                  <Descriptions.Item label="Phone">
                    <a href={`tel:${hospital.phone}`}>
                      <PhoneOutlined /> {hospital.phone}
                    </a>
                  </Descriptions.Item>
                )}
                {hospital.county && (
                  <Descriptions.Item label="County">
                    <EnvironmentOutlined /> {hospital.county} County
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Basic Settings Card */}
            <Card title="Basic Settings">
              {features.length > 0 ? (
                <Space wrap size={[8, 8]}>
                  {features.map((feature, i) => (
                    <Tag key={i} color={feature.color}>
                      {feature.label}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <span style={{ color: '#888' }}>No features configured</span>
              )}
            </Card>

            {/* Configuration Card */}
            <Card title="Configuration">
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Default Radius">
                  {hospital.default_radius !== null ? `${hospital.default_radius} miles` : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Password Expiration">
                  {hospital.password_expiration !== null
                    ? `${hospital.password_expiration} days`
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="MFA Required">
                  {hospital.mfa_required !== null ? (
                    <Tag color={hospital.mfa_required ? 'green' : 'default'}>
                      {hospital.mfa_required ? 'Yes' : 'No'}
                    </Tag>
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
                {hospital.sso && (
                  <Descriptions.Item label="SSO Configured">
                    <Tag color="blue">Yes</Tag>
                  </Descriptions.Item>
                )}
                {hospital.ad && (
                  <Descriptions.Item label="Active Directory">
                    <Tag color="blue">Configured</Tag>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* About Section (if present) */}
            {hospital.about && (
              <Card title="About">
                <p style={{ whiteSpace: 'pre-wrap' }}>{hospital.about}</p>
              </Card>
            )}

            {/* Assigned Senders (for mobile assessment teams only) */}
            {hospital.facility_type?.toLowerCase() === 'mobile assessment team' && (
              <AssignedSenders hospital={hospital} />
            )}
          </Space>
        </Col>

        {/* Right Column - 40% */}
        <Col xs={24} lg={10}>
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            {/* Location Map Card */}
            <Card title="Location">
              {hospital.lat && hospital.lon ? (
                <>
                  <HospitalMap
                    lat={hospital.lat}
                    lon={hospital.lon}
                    name={hospital.name}
                    height={300}
                  />
                  <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                    <EnvironmentOutlined /> Coordinates: {hospital.lat.toFixed(6)},{' '}
                    {hospital.lon.toFixed(6)}
                  </div>
                </>
              ) : (
                <div style={{ padding: '40px 0', textAlign: 'center', color: '#999' }}>
                  No location coordinates available
                </div>
              )}
            </Card>

            {/* System Information Card */}
            <Card
              title="System Information"
              extra={
                <Button size="small" icon={<CodeOutlined />} onClick={handleViewRaw} type="default">
                  View Raw
                </Button>
              }
            >
              <Descriptions column={1} size="small">
                {hospital.market_id && (
                  <Descriptions.Item label="Market ID">{hospital.market_id}</Descriptions.Item>
                )}
                {hospital.org_id && (
                  <Descriptions.Item label="Organization ID">{hospital.org_id}</Descriptions.Item>
                )}
                <Descriptions.Item label="Created">
                  {new Date(hospital.createdAt).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Updated">
                  {new Date(hospital.updatedAt).toLocaleString()}
                </Descriptions.Item>
                <Descriptions.Item label="Hospital ID">
                  <code style={{ fontSize: '11px' }}>{hospital.id}</code>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Space>
        </Col>
      </Row>

      {/* Feature Management Section */}
      <div style={{ marginTop: 24 }}>
        <HospitalFeatures hospitalId={id!} />
      </div>

      {/* Capabilities Management Section */}
      <div style={{ marginTop: 24 }}>
        <HospitalCapabilities hospitalId={id!} />
      </div>

      {/* SSO Configuration & Fax Numbers - Side by Side */}
      <Row gutter={16} style={{ marginTop: 24 }}>
        <Col xs={24} lg={12}>
          <SSOConfiguration hospitalId={id!} />
        </Col>
        <Col xs={24} lg={12}>
          <HospitalFaxNumbers hospitalId={id!} />
        </Col>
      </Row>

      {/* Edit Modal */}
      <Modal
        title="Edit Hospital"
        open={isEditModalOpen}
        onOk={handleSubmit}
        onCancel={handleModalClose}
        width={800}
        confirmLoading={updateMutation.isPending}
        okText="Save Changes"
        cancelText="Cancel"
      >
        <Form form={form} layout="vertical">
          <HospitalForm />
        </Form>
      </Modal>

      {/* Raw JSON Modal */}
      <Modal
        title="Raw Hospital Data"
        open={isRawModalOpen}
        onCancel={handleRawModalClose}
        width={800}
        footer={[
          <Button key="copy" onClick={copyToClipboard}>
            Copy to Clipboard
          </Button>,
          <Button key="close" type="primary" onClick={handleRawModalClose}>
            Close
          </Button>,
        ]}
      >
        <div
          style={{
            maxHeight: '70vh',
            overflow: 'auto',
            backgroundColor: '#f5f5f5',
            padding: '16px',
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
          }}
        >
          <pre
            style={{
              margin: 0,
              fontFamily: "'Fira Code', 'Courier New', 'Monaco', 'Menlo', 'Consolas', monospace",
              fontSize: '13px',
              lineHeight: '1.6',
              color: '#262626',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(hospital, null, 2)}
          </pre>
        </div>
      </Modal>
    </div>
  )
}
